import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

const MENU_GUTTER = 8;
const MENU_OFFSET = 4;
const MENU_MIN_WIDTH = 220;

const InspectionStatusDropdown = ({
  activeStatus,
  onSelect,
  reservationId,
  statuses,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = `inspection-status-menu-${reservationId}`;

  const positionMenu = useCallback(() => {
    if (!buttonRef.current || !menuRef.current) return;

    const triggerRect = buttonRef.current.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();
    const width = Math.max(MENU_MIN_WIDTH, triggerRect.width);
    const maxLeft = Math.max(
      MENU_GUTTER,
      window.innerWidth - width - MENU_GUTTER,
    );
    const maxTop = Math.max(
      MENU_GUTTER,
      window.innerHeight - menuRect.height - MENU_GUTTER,
    );
    const roomBelow = window.innerHeight - triggerRect.bottom - MENU_OFFSET;
    const roomAbove = triggerRect.top - MENU_OFFSET;

    let top = triggerRect.bottom + MENU_OFFSET;
    if (menuRect.height > roomBelow && roomAbove > roomBelow) {
      top = triggerRect.top - menuRect.height - MENU_OFFSET;
    }

    setMenuStyle({
      position: "fixed",
      top: Math.max(MENU_GUTTER, Math.min(top, maxTop)),
      left: Math.max(MENU_GUTTER, Math.min(triggerRect.left, maxLeft)),
      width,
    });
  }, []);

  useLayoutEffect(() => {
    if (isOpen) positionMenu();
  }, [isOpen, positionMenu]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (
        !buttonRef.current?.contains(event.target) &&
        !menuRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    };
  }, [isOpen, positionMenu]);

  const selectStatus = (statusKey) => {
    onSelect(statusKey);
    setIsOpen(false);
    buttonRef.current?.focus();
  };

  return (
    <div className="rbr-inspection-status-cell">
      <button
        ref={buttonRef}
        type="button"
        className={`rbr-inspection-status-select${activeStatus ? ` is-${activeStatus.key} is-active` : ""}`}
        aria-controls={menuId}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        onClick={() => setIsOpen((open) => !open)}
      >
        <span
          className={`rbr-inspection-status-swatch${activeStatus ? ` is-${activeStatus.key}` : ""}`}
        />
        <span className="rbr-inspection-status-label">
          {activeStatus ? activeStatus.label : "Set status"}
        </span>
        <span uk-icon="icon: triangle-down; ratio: 0.85" />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            className="uk-dropdown uk-open rbr-inspection-status-dropdown"
            style={menuStyle}
          >
            <ul className="uk-nav uk-dropdown-nav rbr-inspection-status-menu" role="menu">
              {statuses.map((status) => {
                const active = activeStatus?.key === status.key;
                return (
                  <li
                    key={status.key}
                    className={`is-${status.key}${active ? " is-active" : ""}`}
                  >
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked={active}
                      onClick={() => selectStatus(status.key)}
                    >
                      <span className={`rbr-inspection-status-swatch is-${status.key}`} />
                      <span className="rbr-inspection-status-label">{status.label}</span>
                      {active && (
                        <span
                          className="rbr-inspection-status-check"
                          uk-icon="icon: check; ratio: 0.85"
                        />
                      )}
                    </button>
                  </li>
                );
              })}
              {activeStatus && (
                <>
                  <li className="uk-nav-divider" />
                  <li>
                    <button
                      type="button"
                      className="rbr-inspection-status-clear"
                      onClick={() => selectStatus(activeStatus.key)}
                    >
                      Clear status
                    </button>
                  </li>
                </>
              )}
            </ul>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default InspectionStatusDropdown;
